import {
  type WorkCalendar,
  nextWorkingDay,
  previousWorkingDay,
  requireIsoDate,
  startOfTask,
  workingDaysInclusive,
} from '@pst/core-scheduling';
import type { GroupView, PlanView, RollClaudeChange, SchedDependency, SchedTask, ScheduleView } from '../../api';
import { recalculate } from './localSchedule';

/**
 * Rolling the plan forward to a status date (Fortschreiben): what is done
 * stays where it happened, what runs goes on from today, what is open
 * starts today at the earliest. A fixed rule, no guessing; the result is a
 * preview until someone takes it over. Pure, tested.
 */

export type RollKind = 'erledigt' | 'läuft' | 'offen';

/** One change to one task, as the task update the server takes. */
export interface RollChange {
  taskId: string;
  code: string;
  name: string;
  kind: RollKind;
  body: { startDate?: string; durationDays?: number };
  /** Why, in German, one short line. */
  reason: string;
}

/** What Claude may propose from new information (see the job's schema). */
export type ClaudeChange = RollClaudeChange;

const iso = (s: string) => requireIsoDate(s);

/** The rule. `status` is the status date, usually today. */
export function rollForward(tasks: readonly SchedTask[], status: string, calendar: WorkCalendar): RollChange[] {
  const day = nextWorkingDay(calendar, iso(status));
  const lastWorked = previousWorkingDay(calendar, iso(status));
  const out: RollChange[] = [];
  for (const t of tasks) {
    if (!t.earlyStart || !t.earlyFinish) continue;
    // "Ende nicht vor" is a promise someone made; the rule leaves it alone (named in rollWarnings).
    if (t.constraintType === 'finishNoEarlierThan') continue;
    const pinned = t.constraintType === 'startNoEarlierThan' ? t.constraintDate : null;
    const base = { taskId: t.id, code: t.code, name: t.name };
    if (t.status === 'done') {
      // Done: on its dates, ending on the day it was ticked off (not after today).
      let finish = t.doneOn ?? t.earlyFinish;
      if (finish > lastWorked) finish = lastWorked;
      finish = previousWorkingDay(calendar, iso(finish));
      const start = t.durationDays === 0 ? finish : t.earlyStart <= finish ? t.earlyStart : startOfTask(calendar, iso(finish), Math.max(1, t.durationDays));
      const duration = t.durationDays === 0 ? 0 : workingDaysInclusive(calendar, iso(start), iso(finish));
      const body: RollChange['body'] = {};
      if (pinned !== start) body.startDate = start;
      if (duration !== t.durationDays) body.durationDays = duration;
      if (Object.keys(body).length) out.push({ ...base, kind: 'erledigt', body, reason: `erledigt ${t.doneOn ? 'am' : 'bis'} ${finish}` });
    } else if (t.status === 'running') {
      // Running: from where it began; overdue, it ends today at the earliest.
      const body: RollChange['body'] = {};
      if (pinned !== t.earlyStart) body.startDate = t.earlyStart;
      if (t.earlyFinish < day) body.durationDays = workingDaysInclusive(calendar, iso(t.earlyStart), day);
      if (Object.keys(body).length) out.push({ ...base, kind: 'läuft', body, reason: t.earlyFinish < day ? 'läuft noch, endet frühestens heute' : 'läuft, Beginn festgehalten' });
    } else if (t.earlyStart < day) {
      // Open (or blocked): not before today.
      out.push({ ...base, kind: 'offen', body: { startDate: day }, reason: `offen, frühestens ab ${day}` });
    }
  }
  return out;
}

/** What the rule cannot put right by itself, for the person to look at. */
export function rollWarnings(tasks: readonly SchedTask[], dependencies: readonly SchedDependency[]): string[] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const out: string[] = [];
  for (const t of tasks) {
    if (t.constraintType === 'finishNoEarlierThan') out.push(`${t.code}: Terminvorgabe „Ende nicht vor“ – nicht fortgeschrieben`);
    if (t.status === 'done') {
      const open = dependencies.filter((d) => d.successorId === t.id).map((d) => byId.get(d.predecessorId)).filter((p) => p && p.status !== 'done');
      if (open.length) out.push(`${t.code} ist erledigt, aber ${open.map((p) => p!.code).join(', ')} noch nicht – ${t.code} kann sich dadurch nach hinten verschieben`);
    }
  }
  return out;
}

export const NEW_PREFIX = 'neu:';

/** Everything to take over, in the shape the server takes it; also the base of the preview. */
export interface RollPlan {
  updates: { taskId: string; startDate?: string | null; durationDays?: number }[];
  newTasks: { code: string; name: string; durationDays: number; wbsNodeId: string | null; after: string[]; startDate: string }[];
  /** Task ids, or "neu:<code>" for a new task. */
  links: { from: string; to: string }[];
  skipped: string[];
}

/**
 * The rule's changes and the chosen Claude changes together: only numbers
 * that exist (or are new and chosen), no relationship twice or one already
 * there, new tasks from the status date on.
 */
export function buildRoll(
  current: ScheduleView,
  dependencies: readonly SchedDependency[],
  changes: readonly RollChange[],
  claude: readonly ClaudeChange[],
  status: string,
  calendar: WorkCalendar,
): RollPlan {
  const skipped: string[] = [];
  const day = nextWorkingDay(calendar, iso(status));
  const byCode = new Map(current.tasks.map((t) => [t.code, t]));
  const updates = new Map<string, { taskId: string; startDate?: string | null; durationDays?: number }>();
  for (const c of changes) updates.set(c.taskId, { taskId: c.taskId, ...c.body });
  const fresh = new Set(claude.filter((c) => c.type === 'new' && !byCode.has(c.code)).map((c) => (c as { code: string }).code));
  const idOf = (code: string): string | undefined => byCode.get(code)?.id ?? (fresh.has(code) ? NEW_PREFIX + code : undefined);
  const pairs = new Set(dependencies.map((d) => `${d.predecessorId}>${d.successorId}`));
  const link = (from: string, to: string): boolean => {
    const key = `${from}>${to}`;
    if (from === to || pairs.has(key)) return false;
    pairs.add(key);
    return true;
  };
  const newTasks: RollPlan['newTasks'] = [];
  const links: RollPlan['links'] = [];
  for (const c of claude) {
    if (c.type === 'duration' || c.type === 'start') {
      const t = byCode.get(c.code);
      if (!t) { skipped.push(`${c.code}: unbekannter Vorgang`); continue; }
      const u = updates.get(t.id) ?? { taskId: t.id };
      if (c.type === 'duration') u.durationDays = c.days; else u.startDate = c.date < day ? day : c.date;
      updates.set(t.id, u);
    } else if (c.type === 'new') {
      if (byCode.has(c.code)) { skipped.push(`${c.code}: Nummer gibt es schon`); continue; }
      const after: string[] = [];
      for (const a of c.after) {
        const from = idOf(a);
        if (!from) { skipped.push(`${c.code}: Vorgänger ${a} nicht dabei`); continue; }
        if (link(from, NEW_PREFIX + c.code)) after.push(from);
      }
      const phase = c.phase ? current.groups.find((g) => g.code === c.phase)?.id ?? null : null;
      newTasks.push({ code: c.code, name: c.name, durationDays: c.days, wbsNodeId: phase, after, startDate: day });
    } else {
      const from = idOf(c.from); const to = idOf(c.to);
      if (!from || !to) { skipped.push(`${c.from} → ${c.to}: Vorgang nicht dabei`); continue; }
      if (link(from, to)) links.push({ from, to });
    }
  }
  return { updates: [...updates.values()], newTasks, links, skipped };
}

export interface RollPreview {
  schedule: ScheduleView;
  /** Tasks whose dates change, with before and after. */
  moved: { id: string; code: string; name: string; fromStart: string | null; fromFinish: string | null; toStart: string | null; toFinish: string | null }[];
  finishBefore: string;
  finishAfter: string;
}

/** The plan as it would be after taking `roll` over, calculated in the browser; null for a cycle. */
export function previewRoll(plan: PlanView, current: ScheduleView, dependencies: readonly SchedDependency[], roll: RollPlan, calendar: WorkCalendar): RollPreview | null {
  const body = new Map(roll.updates.map((u) => [u.taskId, u]));
  const tasks: SchedTask[] = current.tasks.map((t) => {
    const u = body.get(t.id);
    if (!u) return t;
    const next = { ...t };
    if (u.durationDays !== undefined) next.durationDays = u.durationDays;
    if (u.startDate !== undefined) { next.constraintType = u.startDate ? 'startNoEarlierThan' : null; next.constraintDate = u.startDate ?? null; }
    return next;
  });
  const deps: SchedDependency[] = [...dependencies];
  for (const n of roll.newTasks) {
    tasks.push({
      id: NEW_PREFIX + n.code, code: n.code, name: n.name, wbsNodeId: n.wbsNodeId, durationDays: n.durationDays, sortOrder: 99_999, note: null, color: null,
      status: 'open', owner: null, position: null, deliverable: null, deadline: null, constraintType: 'startNoEarlierThan', constraintDate: n.startDate,
      earlyStart: null, earlyFinish: null, lateStart: null, lateFinish: null, totalFloat: null, freeFloat: null, critical: null, summaryId: null, doneOn: null,
    });
    for (const a of n.after) deps.push({ id: `${a}>${n.code}`, predecessorId: a, successorId: NEW_PREFIX + n.code, type: 'FS', lagDays: 0 });
  }
  for (const l of roll.links) deps.push({ id: `${l.from}>${l.to}`, predecessorId: l.from, successorId: l.to, type: 'FS', lagDays: 0 });
  const groups: GroupView[] = current.groups;
  const result = recalculate(plan, tasks, deps, groups, calendar);
  if (!result) return null;
  const before = new Map(current.tasks.map((t) => [t.id, t]));
  const moved = result.tasks
    .filter((t) => { const b = before.get(t.id); return !b || b.earlyStart !== t.earlyStart || b.earlyFinish !== t.earlyFinish; })
    .map((t) => ({ id: t.id, code: t.code, name: t.name, fromStart: before.get(t.id)?.earlyStart ?? null, fromFinish: before.get(t.id)?.earlyFinish ?? null, toStart: t.earlyStart, toFinish: t.earlyFinish }));
  return { schedule: { ...result, summaries: current.summaries }, moved, finishBefore: current.earliestFinish, finishAfter: result.earliestFinish };
}
